/**
 * ThemePopupManager
 *
 * Global gatekeeper for theme popups (`auto-modal`, `confirm-modal`).
 *
 * Only one popup may be on screen at a time. A popup that wants to open on its
 * own schedule (delay timer, exit intent) while another one is already up gets
 * put in a queue instead of rendering on top of it. The queue is re-checked
 * `queueDelay` ms after the open popup closes, so the visitor gets a breather
 * between popups instead of a chain of them.
 *
 * Popups opened by an explicit action (trigger button, add-to-cart upsell,
 * post-submit confirmation) take the slot immediately and displace whatever is
 * open, since the visitor asked for them.
 *
 * Override the gap between popups by setting `window.THEME_POPUP_QUEUE_DELAY`
 * (ms) — it is read each time the queue is scheduled.
 *
 * Consumers must tolerate this script being absent — see the `NO_MANAGER` shim
 * in component-auto-modal.js.
 */
if (!window.ThemePopupManager) {
  const DEFAULT_QUEUE_DELAY = 10000

  class ThemePopupManager {
    constructor() {
      this.current = null
      this.queue = []
      this.timer = null
      this.debug = false
    }

    get queueDelay() {
      const override = parseInt(window.THEME_POPUP_QUEUE_DELAY, 10)
      return isNaN(override) ? DEFAULT_QUEUE_DELAY : Math.max(0, override)
    }

    enableDebug() {
      this.debug = true
    }

    _log(...args) {
      if (this.debug) console.log("[ThemePopupManager]", ...args)
    }

    _name(popup) {
      if (!popup) return "unknown"
      return popup.id || popup.nodeName.toLowerCase()
    }

    /**
     * The popup holding the single open slot, if it is genuinely still open.
     * Self-heals when a holder was hidden or removed without releasing.
     */
    getCurrent() {
      if (this.current && !(this.current.isOpen && this.current.isConnected)) {
        this._log("clearing stale slot holder", this._name(this.current))
        this.current = null
      }
      return this.current
    }

    isBusy(popup = null) {
      const current = this.getCurrent()
      return !!current && current !== popup
    }

    /**
     * Take the open slot. Returns false when another popup holds it and
     * `force` is not set, in which case the caller should queue itself.
     */
    claim(popup, { force = false } = {}) {
      const current = this.getCurrent()
      if (current === popup) return true

      if (current) {
        if (!force) {
          this._log(
            `claim denied for ${this._name(popup)} — ${this._name(current)} is open`,
          )
          return false
        }
        this._log(
          `${this._name(popup)} is displacing ${this._name(current)}`,
        )
        current.__themePopupDisplacing = true
        try {
          current.hide()
        } finally {
          delete current.__themePopupDisplacing
        }
      }

      this.remove(popup)
      this.current = popup
      this._log(`claim granted to ${this._name(popup)}`)
      return true
    }

    /** Give up the slot and start the countdown to the next queued popup. */
    release(popup) {
      if (this.current !== popup) return
      this.current = null
      this._log(
        `${this._name(popup)} released the slot, ${this.queue.length} queued`,
      )
      this.scheduleQueue()
    }

    enqueue(popup) {
      if (!popup || this.queue.includes(popup)) return false
      this.queue.push(popup)
      this._log(`queued ${this._name(popup)} (position ${this.queue.length})`)
      if (!this.isBusy() && !this.timer) this.scheduleQueue()
      return true
    }

    remove(popup) {
      const index = this.queue.indexOf(popup)
      if (index > -1) this.queue.splice(index, 1)
    }

    scheduleQueue() {
      if (this.timer) {
        window.clearTimeout(this.timer)
        this.timer = null
      }

      this.queue = this.queue.filter((popup) => popup.isConnected)
      if (!this.queue.length) return

      this._log(`checking the queue in ${this.queueDelay}ms`)
      this.timer = window.setTimeout(
        this.processQueue.bind(this),
        this.queueDelay,
      )
    }

    /**
     * Offer the slot to queued popups in turn. Each one re-runs its own
     * eligibility checks, so anything that no longer wants to show is dropped.
     */
    processQueue() {
      this.timer = null

      if (this.isBusy()) {
        this._log("queue check skipped — a popup is open")
        return
      }

      while (this.queue.length) {
        const popup = this.queue.shift()
        if (!popup.isConnected || typeof popup.tryToOpen !== "function") continue

        this._log(`queue check → trying ${this._name(popup)}`)
        const status = popup.tryToOpen({ fromQueue: true })
        if (status === "opened" || this.isBusy()) return

        this._log(
          `${this._name(popup)} passed on its turn (${status}), dropping it`,
        )
      }
    }
  }

  window.ThemePopupManager = ThemePopupManager
  window.themePopupManager = new ThemePopupManager()
}
